import { acceptBlockedReason } from '../aiThresholds';
import rules from '../../constants/fieldTraitRules.json';

/*
 * Field traits, L16 (docs/LIVE_UX_PLAN.md 3.3): when it's worth asking a person to look at a mushroom's base or stem.
 *
 * Nothing here sees a volva or a veil. No detector for parts exists, and nobody has labelled the pictures to train one,
 * so these rules read the AI's answer and the way the picture was framed, and can only ask. No arrow points at a part:
 * an arrow on the wrong part of a dangerous mushroom misleads.
 *
 * What can't happen, whichever way anyone answers:
 *   - an answer never reaches the result: it goes on the batch and from there to the record's phenotype. The scores, the
 *     order, the safety banner and acceptBlockedReason read the result alone, so they come out as they were.
 *   - an answer never takes an ask away. "None seen" isn't a clearance: a volva stays buried or falls apart.
 *   - no ask isn't a clearance either. It only means no rule fired, and says nothing about the mushroom.
 */
export const TRAITS = Object.freeze(Object.keys(rules.traits));
export const ANSWERS = Object.freeze(['yes', 'no', 'cant_tell']);

// Unmeasured, like the capture gate's thresholds: how near the bottom of what the person can see counts as reaching it.
export const FRAMING = Object.freeze({ edgeShare: 0.06 });

// routes/phenotype.py WRITE_ROLES and OWN_RECORD_ROLES: anyone else's answer would be refused on the way to the record.
const RECORDING_ROLES = new Set(['collection_manager', 'internal_expert', 'lab_technician', 'lab_manager', 'student',
  'collector', 'external_researcher']);

export function mayRecordTraits(user) {
  return (user?.roles || []).some(role => RECORDING_ROLES.has(role));
}

function genusOf(name) {
  return String(name || '').trim().split(/\s+/)[0].toLowerCase();
}

function inRule(name, trait) {
  return rules.traits[trait].genera.some(genus => genus.toLowerCase() === genusOf(name));
}

// A literature profile (routes/taxon_knowledge.py load_profiles) that describes the part as there.
function describedBy(prediction, field) {
  return (prediction?.reference?.traits?.[field] || []).some(row => (
    row.evidence === 'literature' && ['typical', 'occasional'].includes(row.state)
    && ['true', 'yes', 'present'].includes(String(row.value).toLowerCase())));
}

/**
 * What to ask a person to look at, for one AI answer: [{trait, field, why, taxa}], the volva first. `why` is `warning`
 * (a toxic taxon or a dangerous look-alike is among them, so both parts are asked for whatever they are), `literature`
 * or `candidate`. Reads the answer and never writes to it.
 */
export function fieldTraitAsks(result) {
  const predictions = result?.predictions || [];
  if (!predictions.length) return [];
  const warned = acceptBlockedReason(result) === 'dangerous';
  const names = [...predictions.map(p => p.taxon_name), ...(result?.safety?.tail || []).map(x => x.taxon)];
  const asks = [];
  TRAITS.forEach((trait) => {
    const { field } = rules.traits[trait];
    const described = predictions.filter(p => describedBy(p, field)).map(p => p.taxon_name);
    const named = names.filter(name => inRule(name, trait));
    let why = null;
    if (warned) why = 'warning';
    else if (described.length) why = 'literature';
    else if (named.length) why = 'candidate';
    if (why) asks.push({ trait, field, why, taxa: [...new Set(described.length ? described : named)] });
  });
  return asks;
}

/**
 * Whether the base may be out of the picture: the loop reaches the bottom of what the person can see. A hint for the
 * wording of an ask that's already there. It doesn't raise an ask, and a loop well inside the picture says nothing
 * about the base being in it.
 */
export function baseMayBeOutOfView(bounds, viewport, { bottomInset = 0, edgeShare = FRAMING.edgeShare } = {}) {
  if (!bounds || !(viewport?.height > 0)) return false;
  return bounds.top + bounds.height >= viewport.height - bottomInset - viewport.height * edgeShare;
}

/** The asks nobody has answered, with their answers in `answers` ({volva: 'no'}). */
export function unanswered(asks, answers) {
  return asks.filter(ask => !answers?.[ask.trait]);
}

/**
 * A batch with a person's answer on it. `traitChecks` says what was answered; `phenotype` is what the outbox's phenotype
 * step sends (PUT /collections/{id}/phenotype). "Can't tell" sends nothing: on the record, not checked is NULL, never a
 * no (routes/phenotype.py).
 */
export function withTraitAnswer(batch, trait, answer) {
  const { field } = rules.traits[trait];
  const phenotype = { ...batch.phenotype };
  if (answer === 'yes') phenotype[field] = true;
  else if (answer === 'no') phenotype[field] = false;
  else delete phenotype[field];
  const next = { ...batch, traitChecks: { ...batch.traitChecks, [trait]: answer } };
  if (Object.keys(phenotype).length) next.phenotype = phenotype;
  else delete next.phenotype;
  return next;
}

/** Answers given before the specimen had a batch, put on it once it does. */
export function withTraitAnswers(batch, answers) {
  return Object.entries(answers || {}).reduce((next, [trait, answer]) => withTraitAnswer(next, trait, answer), batch);
}
